import React, { useMemo, useState, useCallback } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper/Paper'
import Alert from '@mui/material/Alert'
import CircularProgress from '@mui/material/CircularProgress'
import { KnockStatusChip } from '@user/features/MapView/KnockStatusChip'
import { AccessTime } from '@workwave-tidal/core/icons/AccessTime'
import { format } from 'date-fns'
import { EditPencilIcon } from '@sales-center/components'
import IconButton from '@mui/material/IconButton'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button/Button'
import InputAdornment from '@mui/material/InputAdornment'
import { VoiceInputButton } from '@user/components/VoiceInputButton'
import { useVoiceInput } from '@user/hooks/useVoiceInput'
import { useVoiceToTextEnabled } from '@user/store/tenantFeatures/hooks/useVoiceToTextEnabled'
import { HistoryItem } from './hooks/useHistoryData'

const MAX_REASON_LENGTH = 200

type Props = {
  historyList: HistoryItem[]
  isLoading: boolean
  onUpdate: (args: {
    id: number
    customReason: string | null
    knockStatusName: string | null
    previousKnockStatusName: string | null
  }) => Promise<void>
  onUpdateAddressDetails?: (item: HistoryItem) => void
}

export function KnockStatusHistory({
  historyList,
  isLoading,
  onUpdate,
  onUpdateAddressDetails,
}: Props) {
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editReason, setEditReason] = useState<string>('')
  const [isUpdating, setIsUpdating] = useState(false)
  const voiceToTextEnabled = useVoiceToTextEnabled()

  const handleVoiceResult = useCallback((transcript: string) => {
    setEditReason(transcript.slice(0, MAX_REASON_LENGTH))
  }, [])

  const {
    supported: voiceSupported,
    listening,
    start: startMic,
    stop: stopMic,
  } = useVoiceInput({ onResult: handleVoiceResult })

  const handleMicKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault()
      startMic()
    }
  }

  const handleMicKeyUp = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault()
      stopMic()
    }
  }

  // After a clear (SH-661) the just-cleared row is deactivated but keeps its CreatedTime, so it
  // naturally sorts to the top of this (inactive-only, date-desc) list showing its real
  // status/reason -- matching the AC. No synthetic row: the '-' lives in the card/bubble only.
  const filteredHistoryList = useMemo(
    () => historyList.filter((item) => !item.isActive),
    [historyList],
  )

  const handleStartEdit = (item: HistoryItem) => {
    setEditingId(item.id)
    setEditReason(item.customReason || '')
  }

  const handleCancelEdit = () => {
    setEditingId(null)
    setEditReason('')
  }

  const handleSaveEdit = async (item: HistoryItem) => {
    setIsUpdating(true)
    const sanitizedReason = editReason.trim() || null

    try {
      await onUpdate({
        id: item.id,
        customReason: sanitizedReason,
        knockStatusName: item.knockStatusName,
        previousKnockStatusName: item.knockStatusName,
      })
      setEditingId(null)
      setEditReason('')
      if (item.isActive) {
        const updatedItem = { ...item, customReason: sanitizedReason || '' }
        onUpdateAddressDetails?.(updatedItem)
      }
    } finally {
      setIsUpdating(false)
    }
  }

  return (
    <Box
      sx={{
        pb: 0,
      }}
    >
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={24} />
        </Box>
      ) : filteredHistoryList.length === 0 ? (
        <Alert severity="info" sx={{ mt: 1.6 }}>
          No history
        </Alert>
      ) : (
        filteredHistoryList.map((item) => {
          const isEditing = editingId === item.id
          const statusName = item.knockStatusName || 'None'
          const isDefault = item.isDefault

          return (
            <Paper
              key={item.id}
              variant={isEditing ? 'elevation' : 'outlined'}
              elevation={isEditing ? 3 : 0}
              sx={{
                mb: 1.2,
                px: 1.6,
                py: 1.6,
                '&:last-of-type': { mb: 0 },
              }}
            >
              <Box sx={{ position: 'relative' }}>
                {!isEditing && (
                  <IconButton
                    sx={{
                      position: 'absolute',
                      right: 0,
                      top: 0,
                    }}
                    onClick={() => handleStartEdit(item)}
                    size="medium"
                  >
                    <EditPencilIcon />
                  </IconButton>
                )}

                <Box sx={{ display: 'flex', gap: 0.8, mb: 0.8 }}>
                  <KnockStatusChip status={statusName} isDefault={isDefault} />
                </Box>

                {isEditing ? (
                  <Box sx={{ mt: 0.4 }}>
                    <TextField
                      fullWidth
                      label="Reason"
                      value={editReason}
                      onChange={(e) =>
                        setEditReason(
                          e.target.value.slice(0, MAX_REASON_LENGTH),
                        )
                      }
                      disabled={isUpdating}
                      multiline
                      inputProps={{
                        maxLength: MAX_REASON_LENGTH,
                      }}
                      minRows={1}
                      maxRows={2}
                      helperText={`${editReason.length}/${MAX_REASON_LENGTH}`}
                      InputProps={
                        voiceToTextEnabled && voiceSupported
                          ? {
                              endAdornment: (
                                <InputAdornment position="end">
                                  <VoiceInputButton
                                    listening={listening}
                                    onMouseDown={startMic}
                                    onMouseUp={stopMic}
                                    onKeyDown={handleMicKeyDown}
                                    onKeyUp={handleMicKeyUp}
                                    disabled={isUpdating}
                                    ariaLabel="hold to speak reason"
                                  />
                                </InputAdornment>
                              ),
                            }
                          : undefined
                      }
                    />
                  </Box>
                ) : (
                  <Typography
                    variant="body2"
                    sx={{
                      mt: 0.4,
                      whiteSpace: 'normal',
                      overflowWrap: 'anywhere',
                    }}
                  >
                    <Typography component="span" variant="subtitle4">
                      Reason:
                    </Typography>
                    {'  '}
                    {item.customReason || '-'}
                  </Typography>
                )}

                <Typography
                  variant="body2"
                  sx={{
                    mt: 0.8,
                    color: '#0E283FB2',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  <AccessTime fontSize="small" />
                  {'  '}
                  {item.createdTime
                    ? format(new Date(item.createdTime), 'MMM d, yyyy, HH:mm')
                    : '-'}
                  {item.createdByName ? ` - ${item.createdByName}` : ''}
                </Typography>

                {isEditing && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      gap: 0.5,
                      mt: 0.8,
                    }}
                  >
                    <Button
                      variant="text"
                      type="button"
                      onClick={handleCancelEdit}
                      disabled={isUpdating}
                      sx={{
                        color: '#1565C0',
                        fontWeight: 400,
                        letterSpacing: '0.15px',
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      variant="text"
                      onClick={() => handleSaveEdit(item)}
                      disabled={isUpdating}
                      sx={{
                        color: '#1565C0',
                        fontWeight: 400,
                        letterSpacing: '0.15px',
                      }}
                    >
                      Save
                    </Button>
                  </Box>
                )}
              </Box>
            </Paper>
          )
        })
      )}
    </Box>
  )
}
